import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, Inbox, Check, CalendarDays, Users, Euro } from "lucide-react";
import client, { TOKEN_KEY } from "../lib/api";

const statusLabel = { nieuw: "Nieuw", behandeld: "Behandeld" };
const bookingStatusLabel = { aanvraag: "Aanvraag (offerte sturen)", bevestigd: "Bevestigd", geannuleerd: "Geannuleerd", behandeld: "Behandeld" };
const bookingStatusStyle = {
  bevestigd: "bg-primary/15 text-primary",
  aanvraag: "bg-amber-500/15 text-amber-400",
  geannuleerd: "bg-destructive/10 text-destructive",
  behandeld: "bg-secondary/15 text-secondary",
};
const euro = (v) => "€ " + Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminDashboard() {
  const [tab, setTab] = useState("boekingen");
  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([client.get("/admin/requests"), client.get("/admin/bookings")]);
      setRequests(r.data);
      setBookings(b.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin");
  };

  const toggleRequest = async (req) => {
    const nieuw = req.status === "nieuw" ? "behandeld" : "nieuw";
    await client.patch(`/admin/requests/${req.id}`, { status: nieuw });
    setRequests((rs) => rs.map((r) => (r.id === req.id ? { ...r, status: nieuw } : r)));
  };

  const setBookingStatus = async (b, status) => {
    await client.patch(`/admin/bookings/${b.id}`, { status });
    setBookings((bs) => bs.map((x) => (x.id === b.id ? { ...x, status } : x)));
  };

  const nieuwCount = requests.filter((r) => r.status === "nieuw").length;
  const bevestigdCount = bookings.filter((b) => b.status === "bevestigd").length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Beheer De Proatkoele</h1>
            <p className="text-sm text-muted-foreground">{bookings.length} boekingen · {requests.length} aanvragen</p>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-foreground/80 hover:text-primary font-medium">
            <LogOut size={18} /> Uitloggen
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="inline-flex bg-card border border-border rounded-full p-1">
          <button data-testid="tab-boekingen" onClick={() => setTab("boekingen")} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "boekingen" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}>
            Boekingen ({bevestigdCount} bevestigd)
          </button>
          <button data-testid="tab-aanvragen" onClick={() => setTab("aanvragen")} className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "aanvragen" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-primary"}`}>
            Aanvragen ({nieuwCount} nieuw)
          </button>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <p className="text-muted-foreground">Laden...</p>
        ) : tab === "boekingen" ? (
          bookings.length === 0 ? (
            <div data-testid="admin-bookings-empty" className="text-center py-20">
              <CalendarDays size={40} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Er zijn nog geen boekingen.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-bookings-list">
              {bookings.map((b) => (
                <article key={b.id} data-testid={`admin-booking-${b.id}`} className={`bg-card border rounded-2xl p-6 ${b.status === "bevestigd" ? "border-primary/40" : "border-border"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-heading text-lg font-semibold">{b.naam}</h3>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${bookingStatusStyle[b.status] || "bg-muted text-muted-foreground"}`}>
                          {bookingStatusLabel[b.status] || b.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1">
                        <span className="inline-flex items-center gap-1"><CalendarDays size={14} className="text-primary" /> {b.datum} · {b.dagdeel}</span>
                        <span className="inline-flex items-center gap-1"><Users size={14} className="text-primary" /> {b.aantal_personen} pers.</span>
                        <span>{b.arrangement_label}{b.thema ? ` — ${b.thema}` : ""}</span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                        <Euro size={14} /> Totaal (indicatief) {euro(b.totaal)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {b.status !== "bevestigd" && (
                        <button onClick={() => setBookingStatus(b, "bevestigd")} className="text-sm font-medium border border-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">Bevestigen</button>
                      )}
                      {b.status !== "geannuleerd" && (
                        <button onClick={() => setBookingStatus(b, "geannuleerd")} className="text-sm font-medium border border-border rounded-full px-3 py-1.5 hover:border-destructive hover:text-destructive transition-colors">Annuleren</button>
                      )}
                    </div>
                  </div>
                  {b.bericht && <p className="mt-4 text-foreground/90 whitespace-pre-line leading-relaxed">{b.bericht}</p>}
                  <div className="mt-4 flex flex-wrap gap-4 text-sm">
                    <a href={`mailto:${b.email}`} className="inline-flex items-center gap-2 text-primary hover:underline"><Mail size={15} /> {b.email}</a>
                    <a href={`tel:${b.telefoon}`} className="inline-flex items-center gap-2 text-primary hover:underline"><Phone size={15} /> {b.telefoon}</a>
                  </div>
                </article>
              ))}
            </div>
          )
        ) : requests.length === 0 ? (
          <div data-testid="admin-empty" className="text-center py-20">
            <Inbox size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Er zijn nog geen aanvragen binnengekomen.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-requests-list">
            {requests.map((r) => (
              <article key={r.id} data-testid={`admin-request-${r.id}`} className={`bg-card border rounded-2xl p-6 ${r.status === "nieuw" ? "border-primary/40" : "border-border"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-lg font-semibold">{r.naam}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.status === "nieuw" ? "bg-primary/15 text-primary" : "bg-secondary/15 text-secondary"}`}>
                        {statusLabel[r.status] || r.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{r.soort_bijeenkomst} · {new Date(r.created_at).toLocaleString("nl-NL")}</p>
                    {(r.datum || r.aantal_personen) && (
                      <p className="text-sm font-medium text-foreground mt-1">
                        {r.datum && <span>Datum: {new Date(r.datum).toLocaleDateString("nl-NL")}</span>}
                        {r.datum && r.aantal_personen && <span> · </span>}
                        {r.aantal_personen && <span>{r.aantal_personen} personen</span>}
                      </p>
                    )}
                  </div>
                  <button onClick={() => toggleRequest(r)} data-testid={`admin-toggle-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium border border-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">
                    <Check size={15} /> {r.status === "nieuw" ? "Markeer behandeld" : "Heropen"}
                  </button>
                </div>
                <p className="mt-4 text-foreground/90 whitespace-pre-line leading-relaxed">{r.bericht}</p>
                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 text-primary hover:underline"><Mail size={15} /> {r.email}</a>
                  <a href={`tel:${r.telefoon}`} className="inline-flex items-center gap-2 text-primary hover:underline"><Phone size={15} /> {r.telefoon}</a>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
